<template>
  <v-container fluid>
    <v-alert
      v-model="emailResent"
      dismissible
      prominent
      type="success"
      transition="scale-transition"
    >
      Portal activation email resent
    </v-alert>
    <v-alert
      v-model="emailNotResent"
      dismissible
      prominent
      type="error"
      transition="scale-transition"
    >
      Unable to resend portal activation email
    </v-alert>
    <v-dialog
      v-model="newUserDialog"
      max-width="500px"
    >
      <v-text-field
        v-model="createdUser.email"
        label="email"
        readonly
      />
      <v-text-field
        v-model="createdUser.firstLoginCode"
        label="firstLoginCode"
        readonly
      />
      <v-btn
        color="blue darken-1"
        text
        ripple
        @click="newUserDialog = false"
      >
        Ok
      </v-btn>
    </v-dialog>
    <v-dialog
      v-model="deleteDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Confirm</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <span>Are you sure you want to delete
                {{ confirmDelete.firstName }} {{ confirmDelete.lastName }}?</span>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            ripple
            @click="deleteDialog = !deleteDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            ripple
            @click="deleteItem(confirmDelete)"
          >
            Yes, Delete
          </v-btn>
        </v-card-actions>
        <v-card-actions />
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="newUserDialog"
      max-width="500px"
    >
      <new-user-form @userCreated="addUser" />
    </v-dialog>
    <v-data-table
      :headers="headers"
      :items="users"
      :loading="searchPending"
      :items-per-page="-1"
      loading-text="loading, please wait"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-row>
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Filter table"
              class="mr-4 text-left"
            />
            <v-menu
              top
              origin="center center"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs}">
                <v-btn
                  text
                  color="primary"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Actions
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="newUserDialog = true"
                >
                  New User
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>
        </v-toolbar>
      </template>
      <template v-slot:progress>
        <v-progress-linear
          v-show="searchPending"
          indterminate
          rounded
          height="10"
        />
      </template>
      <template v-slot:item.actions="props">
        <td class="justify-center layout px-0">
          <v-icon
            small
            class="mr-2"
            @click="editItem(props.item)"
          >
            mdi-pencil-box-multiple
          </v-icon>
          <v-icon
            small
            class="mr-2"
            @click="showConfirmDelete(props.item)"
          >
            mdi-delete
          </v-icon>
          <v-btn
            v-if="isPortalUserTable"
            text
            color="primary"
            dark
            @click="resendPortalEmail(props.item)"
          >
            Resend Portal Email
          </v-btn>
        </td>
      </template>
      <template v-slot:item.disabled="{ item }">
        <v-simple-checkbox
          :value="item.disabled"
          disabled
        />
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';

import UserService from '../services/user.service';
import NewUserForm from './NewUserForm.vue';
import User from '../models/user';

export default {
  name: 'Users',
  components: {
    NewUserForm,
  },
  props: {
    isPortalUserTable: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    emailResent: false,
    emailNotResent: false,
    editDialog: false,
    fullPage: true,
    searchPending: false,
    search: '',
    noData: false,
    deleteDialog: false,
    editedIndex: -1,
    editedItem: new User(),
    confirmDelete: new User(),
    newUserDialog: false,
    createdUser: {
      email: '',
      firstLoginCode: '',
    },
    users: [],
  }),
  computed: {
    formTitle() {
      return `${this.editedItem.firstName} ${this.editedItem.lastName}`;
    },
    headers() {
      const baseHeaders = [
        {
          text: 'First Name',
          value: 'firstName',
        },
        {
          text: 'Last Name',
          value: 'lastName',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Phone',
          value: 'phone',
        },
        {
          text: 'Disabled',
          value: 'disabled',
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ];

      if (this.isPortalUserTable) {
        const icnColumn = {
          text: 'Client ICN',
          value: 'client.icnAbbreviation',
        };

        baseHeaders.splice(3, 0, icnColumn);
      }

      return baseHeaders;
    },
  },
  watch: {
    editDialog(val) {
      this.editDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
    deleteDialog(val) {
      this.deleteDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
  },
  mounted() {
    this.getUsers();

    if (this.isPortalUserTable) {
      this.headers.unshift({ text: 'Client', value: 'client.name' });
    }
  },
  methods: {
    ...mapActions('apiClient', ['get', 'create', 'update', 'del']),
    ...mapMutations('apiClient', ['dismissError']),
    closeEditDialog() {
      this.editDialog = false;
    },
    closeDeleteDialog() {
      this.showConfirmDeleteDialog = false;
    },
    editItem(item) {
      this.$router.push({ name: 'userEdit', params: { userId: item.userId } });
    },
    addUser(newUser) {
      this.$router.push({ name: 'userEdit', params: { inputUser: newUser, userId: newUser.userId } });
    },
    async getUsers() {
      this.searchPending = true;

      const type = this.isPortalUserTable ? 'portal' : 'admin';

      await this.get({ url: `/users?type=${type}` }).then(
        (response) => {
          this.users = response.data;
        },
        (error) => {
          console.log(error);
        },
      );

      this.searchPending = false;
    },
    async save(item) {
      await this.update({ url: '/users', body: item, id: `${item.userId}` }).then(
        (response) => {
          this.users[this.editedIndex] = response.data;
          this.editDialog = false;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    showConfirmDelete(item) {
      this.deleteDialog = true;
      this.confirmDelete = item;
    },
    async deleteItem(item) {
      this.isLoading = true;

      await UserService.deleteUser(item).then(
        (response) => {
          this.users.pop(response);
        },
        (error) => {
          console.log(error);
        },
      ).then(() => {
        this.deleteDialog = false;
        this.isLoading = false;
      });
    },
    async resendPortalEmail(item) {
      await this.create({ url: `/users/resendActivationEmail/${item.userId}` }).then(
        (response) => {
          if (response.data.successful) {
            this.emailResent = true;
          } else {
            this.emailNotResent = true;
          }
        },
        (error) => {
          this.emailNotResent = true;
          console.log(error);
        },
      );
    },
  },
};
</script>
